// ---------------------------------------------------------------------------
// Response guard
// ---------------------------------------------------------------------------

/**
 * Asserts that a single-item SDK response contains a valid item.
 * Accepts the `IDeliveryNetworkResponse` returned by `.toPromise()`.
 * Throws a descriptive error when the response is empty or malformed.
 *
 * Usage:
 * ```ts
 * const response = await client.item<Page>(codename).toPromise()
 * const item = assertItemResponse(response, "Page")
 * ```
 */
export function assertItemResponse<T>(
  response: { data: { item: T } },
  label: string
): T {
  if (!response?.data?.item) {
    console.error(`No response data for ${label}:`, response)
    throw new Error(`No data was returned from Kontent for ${label}.`)
  }
  return response.data.item
}

// ---------------------------------------------------------------------------
// Fetch wrapper
// ---------------------------------------------------------------------------

/**
 * Wraps an async SDK call with standardised error logging and re-throw.
 *
 * Usage:
 * ```ts
 * return fetchKontent("Page", async () => {
 *   const response = await client.item<Page>(codename).toPromise()
 *   return assertItemResponse(response, "Page")
 * })
 * ```
 */
export async function fetchKontent<T>(
  label: string,
  fn: () => Promise<T>
): Promise<T> {
  try {
    return await fn()
  } catch (error) {
    console.error(`Error fetching ${label}:`, error)
    throw new Error(`Error fetching ${label}.`)
  }
}
